// Firebase configuration
import { initializeApp } from 'firebase/app';
import { getDatabase, get, onValue, push, ref, remove, set, update } from 'firebase/database';

const firebaseConfig = {
  apiKey: "AIzaSyDuxUjk0ud1vUHPGX1fEcen9j4hq-tCz6Q",
  authDomain: "dart-score-board.firebaseapp.com",
  databaseURL: "https://dart-score-board-default-rtdb.firebaseio.com",
  projectId: "dart-score-board",
  storageBucket: "dart-score-board.firebasestorage.app",
  messagingSenderId: "853792937569",
  appId: "1:853792937569:web:3bf06674e5a0061a83bc20"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

// Game functions
export const createGame = async (gameName, gameType) => {
  const gameId = Math.random().toString(36).substring(2, 8);
  const gameRef = ref(database, `games/${gameId}`);
  
  await set(gameRef, {
    name: gameName,
    type: gameType,
    createdAt: Date.now(),
    active: true,
    players: {},
    currentTurn: null,
    stats: {
      startTime: Date.now(),
      rounds: 0,
      highestScore: 0,
      totalScores: 0
    },
    winner: null
  });
  
  return gameId;
};

export const joinGame = async (gameId, playerName) => {
  // First get the game type
  const gameRef = ref(database, `games/${gameId}`);
  const snapshot = await get(gameRef);
  const gameData = snapshot.val();
  
  if (!gameData) {
    throw new Error('Game not found');
  }
  
  const gameType = gameData.type;
  
  const playerRef = ref(database, `games/${gameId}/players`);
  const newPlayerRef = push(playerRef);
  
  // Set initial score based on game type
  let initialScore = 0;
  if (gameType === '501') initialScore = 501;
  else if (gameType === '301') initialScore = 301;
  else if (gameType === '201') initialScore = 201;
  
  await set(newPlayerRef, {
    name: playerName,
    score: initialScore,
    turns: [],
    lastScores: [], // For storing the last score input details
    joinedAt: Date.now()
  });
  
  return newPlayerRef.key;
};

// Real-time listener for game data
export const getGameData = (gameId, callback, errorCallback) => {
  const gameRef = ref(database, `games/${gameId}`);
  return onValue(gameRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      callback(data);
    } else if (errorCallback) {
      errorCallback(new Error('Game not found'));
    }
  }, (error) => {
    if (errorCallback) {
      errorCallback(error);
    }
  });
};

// One-time fetch of game data
export const fetchGameData = async (gameId) => {
  const gameRef = ref(database, `games/${gameId}`);
  const snapshot = await get(gameRef);
  const data = snapshot.val();
  
  if (!data) {
    throw new Error('Game not found');
  }
  
  return data;
};

export const verifyPlayerInGame = async (gameId, playerId) => {
  const playerRef = ref(database, `games/${gameId}/players/${playerId}`);
  const snapshot = await get(playerRef);
  return snapshot.exists();
};

export const updateScore = async (gameId, playerId, score) => {
  const playerRef = ref(database, `games/${gameId}/players/${playerId}`);
  await update(playerRef, { score });
};

export const recordTurn = async (gameId, playerId, points, dartScores, isBust = false) => {
  const turnRef = ref(database, `games/${gameId}/players/${playerId}/turns`);
  const newTurnRef = push(turnRef);
  
  await set(newTurnRef, {
    points,
    dartScores, // Store individual dart scores
    timestamp: Date.now(),
    bust: isBust // Flag to indicate if this was a bust
  });
  
  // Update the last scores
  const lastScoresRef = ref(database, `games/${gameId}/players/${playerId}/lastScores`);
  await set(lastScoresRef, dartScores);
  
  return newTurnRef.key;
};

// Function to update the last turn
export const updateLastTurn = async (gameId, playerId, points, dartScores) => {
  // Get the player's turns
  const turnsRef = ref(database, `games/${gameId}/players/${playerId}/turns`);
  const snapshot = await get(turnsRef);
  const turns = snapshot.val();
  
  if (!turns) {
    throw new Error('No turns found to edit');
  }
  
  // Get the last turn key
  const turnKeys = Object.keys(turns);
  const lastTurnKey = turnKeys[turnKeys.length - 1];
  
  if (!lastTurnKey) {
    throw new Error('No previous turn found to edit');
  }
  
  // Update the last turn
  const lastTurnRef = ref(database, `games/${gameId}/players/${playerId}/turns/${lastTurnKey}`);
  await update(lastTurnRef, {
    points,
    dartScores,
    edited: true,
    editTimestamp: Date.now()
  });
  
  // Update the last scores
  const lastScoresRef = ref(database, `games/${gameId}/players/${playerId}/lastScores`);
  await set(lastScoresRef, dartScores);
  
  return lastTurnKey;
};

export const setCurrentTurn = async (gameId, playerId) => {
  const gameRef = ref(database, `games/${gameId}`);
  await update(gameRef, { currentTurn: playerId });
};

export const deleteGame = async (gameId) => {
  const gameRef = ref(database, `games/${gameId}`);
  await remove(gameRef);
};

// Function to update game statistics
export const updateGameStats = async (gameId, points) => {
  const statsRef = ref(database, `games/${gameId}/stats`);
  const snapshot = await get(statsRef);
  const stats = snapshot.val();
  
  if (stats) {
    const updatedStats = {
      ...stats,
      rounds: stats.rounds + 1,
      highestScore: Math.max(stats.highestScore, points),
      totalScores: stats.totalScores + points
    };
    
    await update(statsRef, updatedStats);
  }
};

// Function to set the winner and end the game
export const setGameWinner = async (gameId, playerId) => {
  const gameRef = ref(database, `games/${gameId}`);
  const statsRef = ref(database, `games/${gameId}/stats`);
  
  // Get player name
  const playerRef = ref(database, `games/${gameId}/players/${playerId}`);
  const playerSnapshot = await get(playerRef);
  const player = playerSnapshot.val();
  
  if (player) {
    // Update game with winner and end time
    await update(gameRef, { 
      winner: playerId,
      active: false
    });
    
    // Update stats with end time
    await update(statsRef, {
      endTime: Date.now()
    });
  }
};
